import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { clearVerifiedStatusCache } from '../hooks/useVerifiedStatus';

const AuthContext = createContext();
const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes
const WARNING_THRESHOLD = 4.5 * 60 * 1000; // Show warning at 4.5 minutes
const CHECK_INTERVAL = 1000; // Check inactivity every 1 second
const ACTIVITY_THROTTLE = 5000; // Throttle activity updates to once per 5 seconds
const BROADCAST_CHANNEL_NAME = 'wellis_auth_activity';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inactivityWarning, setInactivityWarning] = useState(false);
  const [inactivityNotification, setInactivityNotification] = useState(false);

  // Use ref for last activity timestamp (doesn't need to trigger re-renders)
  const lastActivityTimeRef = useRef(Date.now());
  const warningShownRef = useRef(false);
  const checkIntervalRef = useRef(null);
  const broadcastChannelRef = useRef(null);
  const lastThrottledActivityRef = useRef(0);

  // Initialize cross-tab communication
  useEffect(() => {
    if (!user) return;

    try {
      broadcastChannelRef.current = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      broadcastChannelRef.current.onmessage = (event) => {
        if (event.data.type === 'activity') {
          lastActivityTimeRef.current = event.data.timestamp;
          warningShownRef.current = false;
        }
      };
    } catch (err) {
      console.warn('BroadcastChannel not available, cross-tab sync disabled:', err);
    }

    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
      }
    };
  }, [user]);

  const logout = useCallback((reason = null) => {
    localStorage.removeItem('token');
    setUser(null);
    setToken(null);
    setInactivityWarning(false);
    warningShownRef.current = false;
    clearVerifiedStatusCache();

    if (reason === 'inactivity') {
      setInactivityNotification(true);
      setTimeout(() => setInactivityNotification(false), 8000);
    }
  }, []);

  const recordActivity = useCallback(() => {
    const now = Date.now();
    const timeSinceLastThrottle = now - lastThrottledActivityRef.current;

    // Only update activity timestamp once per 5 seconds (throttle)
    if (timeSinceLastThrottle < ACTIVITY_THROTTLE) {
      return;
    }

    lastThrottledActivityRef.current = now;
    lastActivityTimeRef.current = now;
    warningShownRef.current = false;
    setInactivityWarning(false);

    // Broadcast activity to other tabs
    if (broadcastChannelRef.current) {
      try {
        broadcastChannelRef.current.postMessage({
          type: 'activity',
          timestamp: now,
        });
      } catch (err) {
        console.warn('Failed to broadcast activity:', err);
      }
    }
  }, []);

  const extendSession = useCallback(() => {
    recordActivity();
  }, [recordActivity]);

  // Initialize on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      try {
        const decoded = JSON.parse(atob(storedToken.split('.')[1]));
        setUser(decoded);
        setToken(storedToken);
      } catch (err) {
        console.error('Failed to decode token:', err);
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  }, []);

  // Redirect to forced password reset if flag is set
  useEffect(() => {
    if (user?.must_reset_password && window.location.pathname !== '/force-reset-password') {
      window.location.href = '/force-reset-password';
    }
  }, [user]);

  // Set up inactivity monitoring and activity listeners
  useEffect(() => {
    if (!user) {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      return;
    }

    // Reset activity timestamp on login
    lastActivityTimeRef.current = Date.now();
    warningShownRef.current = false;
    lastThrottledActivityRef.current = Date.now();

    // Check inactivity every 1 second based on elapsed time
    checkIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityTimeRef.current;

      // Logout if elapsed time exceeds 5 minutes
      if (elapsed >= INACTIVITY_TIMEOUT) {
        console.warn('User inactive for 5 minutes, logging out');
        logout('inactivity');
        return;
      }

      // Show warning if between 4.5 and 5 minutes, and hasn't been shown yet
      if (elapsed >= WARNING_THRESHOLD && !warningShownRef.current) {
        warningShownRef.current = true;
        setInactivityWarning(true);
      }
    }, CHECK_INTERVAL);

    // Track user activity with throttling
    const events = ['mousemove', 'click', 'keypress', 'scroll', 'touchmove'];

    events.forEach(event => {
      document.addEventListener(event, recordActivity);
    });

    // Handle visibility changes (tab switching, minimizing)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab became visible; record activity to reset timer
        recordActivity();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      events.forEach(event => {
        document.removeEventListener(event, recordActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, logout, recordActivity]);

  const value = {
    user,
    token,
    loading,
    logout,
    extendSession,
    isAuthenticated: !!user,
    inactivityWarning,
    inactivityNotification,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
